/**
 * Which setup steps are done — one rule per step, from real data. The rail
 * (Shell.tsx), the step pages (OnboardingPage.tsx) and the dashboard's
 * "steps left" / "Continue setup" all read this module, so they cannot
 * disagree.
 *
 * The rail used to mark a step done when the partner pressed Continue on it,
 * remembered per browser. From another browser (or after the AI agent did the
 * work) only Verify showed done, the dashboard said "8 setup steps left", and
 * "Continue setup" went back to step 1 (portal audit, 2026-09-27). Now:
 *
 *   Trades & area     a config has been published
 *   Who installs it   an option was chosen here, a developer was invited, the
 *                     hosted page is live, or the widget is already installed
 *   Install           installed (below)
 *   Verify            installed (below). Ownership (the tier-3 file) is its
 *                     own, optional line on the Verify page and never
 *                     completes the step by itself
 *   Branding & copy   OPTIONAL (2026-09-27 second audit): the defaults work,
 *                     so it never counts as "left". Done once published with
 *                     a logo, colour or headline, or the partner moved on
 *   Where leads go    an active destination, or the published setup collects
 *                     no contact details (so there are no leads to send)
 *   Contractors       optional
 *   Test a lead       a test lead has arrived
 *
 * The hosted page (no website of their own) skips Install and Verify.
 *
 * INSTALLED, ONE DEFINITION (`installState`): the widget loaded on the site
 * (a tier-0 impression, "Live") OR a page check passed (tier 1 or 2). The
 * rail, the Verify page's summary and its "Verified ✓" button, and the
 * dashboard all read it. They used to disagree: a site that was Live with
 * ownership verified but no page check showed Verify ✓ on the rail and
 * "Verified ✓" on the button while the summary said "Install not confirmed"
 * (second portal audit, 2026-09-27).
 *
 * `marks` are the steps this browser pressed Continue on. They decide only
 * the two steps that are choices rather than facts (Who installs it, Branding
 * & copy) — and every step while the facts are unavailable (an API from
 * before `/partner/setup-progress`, or a failed read), which is the old
 * behaviour rather than "nothing done".
 */
import type { PartnerRole, SetupProgress } from '../api/types';
import { stepsFor, type SetupStep } from './steps';

export type StepStatus = 'done' | 'todo' | 'optional';

export interface InstallFacts {
  /** Latest accepted impression (tier 0), if any. */
  last_impression_at: string | null;
  /** A page check (tier 1 or 2) found the widget with this key. */
  install_check_passed: boolean;
  /** The ownership file (tier 3) was found, or an origin is verified. */
  ownership_verified: boolean;
}

export interface InstallState {
  /** The one "Installed ✓": live impressions or a passing page check. */
  installed: boolean;
  live: boolean;
  checkPassed: boolean;
  /** Separate and optional: never makes the site "installed" on its own. */
  owned: boolean;
}

export function installState(f: InstallFacts): InstallState {
  const live = Boolean(f.last_impression_at);
  const checkPassed = f.install_check_passed;
  return { installed: live || checkPassed, live, checkPassed, owned: f.ownership_verified };
}

export function isStepDone(slug: string, facts: SetupProgress | null, marks: readonly string[]): boolean {
  if (!facts) return marks.includes(slug);
  const hosted = facts.hosted_page_served;
  const { installed } = installState(facts);
  switch (slug) {
    case 'trades':
      return facts.published_version !== null;
    case 'installer':
      return marks.includes('installer') || facts.developer_invited || hosted || installed;
    case 'install':
      return installed || hosted;
    case 'verify':
      return installed || hosted;
    case 'branding':
      return facts.branding_customised || marks.includes('branding');
    case 'routing':
      return facts.active_destinations > 0 || facts.collects_contact === false;
    case 'test':
      return facts.test_lead_received;
    default:
      return marks.includes(slug);
  }
}

export function stepStatus(step: SetupStep, facts: SetupProgress | null, marks: readonly string[]): StepStatus {
  if (isStepDone(step.slug, facts, marks)) return 'done';
  return step.optional ? 'optional' : 'todo';
}

/** The required steps this role still has to do, in order. Optional steps never count. */
export function remainingSteps(role: PartnerRole | null, facts: SetupProgress | null, marks: readonly string[]): SetupStep[] {
  return stepsFor(role).filter((s) => !s.optional && !isStepDone(s.slug, facts, marks));
}

/** Where "Continue setup" goes: the first incomplete required step, or null when setup is done. */
export function continueTarget(role: PartnerRole | null, facts: SetupProgress | null, marks: readonly string[]): string | null {
  const next = remainingSteps(role, facts, marks)[0];
  return next ? `/setup/${next.slug}` : null;
}
